// Article Outline page — split-pane comparison of the article's editable
// outline (left) against the full article rendered read-only (right). Click a
// section to scroll the article to that heading; reorder/remove/add sections,
// find GSC/fan-out-grounded coverage gaps and ⚠ thought jumps, then
// "Improve from outline" hands the edited outline back to ArticleDetailPage
// via location.state and its existing rewrite preview runs there.
import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowUp, ArrowDown, X, Plus, Sparkles, ChevronDown, AlertTriangle,
} from "lucide-react";
import { createStaticEditor } from "platejs/static";
import * as api from "../lib/api";
import { htmlToValue } from "@/lib/plate-html";
import { BaseEditorKit } from "@/components/editor/editor-base-kit";
import { EditorStatic } from "@/components/ui/editor-static";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { useArticleOutline, SOURCE_BADGE } from "../hooks/useArticleOutline";

function SectionRow({ s, i, last, totalWords, canScroll, onScroll, onMove, onRemove }) {
  const [open, setOpen] = useState(false);
  const pct = totalWords ? Math.round(((s.words || 0) / totalWords) * 100) : 0;
  const BodyTag = canScroll ? "button" : "div";
  return (
    <div style={{ paddingLeft: `${(Math.max(s.level, 2) - 2) * 12}px` }}>
      <div className="flex items-center gap-1 group">
        <BodyTag
          onClick={canScroll ? onScroll : undefined}
          title={canScroll ? "Scroll to this section in the article" : undefined}
          className={`flex-1 min-w-0 flex items-center gap-1.5 text-left rounded-md px-1.5 py-1 ${canScroll ? "hover:bg-zinc-100 transition-colors cursor-pointer" : ""}`}
        >
          <span className="text-xs font-bold text-zinc-800 truncate">{s.heading}</span>
          {s.added && (
            <span className="px-1 py-px text-[8px] font-bold uppercase rounded bg-indigo-50 text-indigo-700 flex-shrink-0">New</span>
          )}
          {s.level === 0 && (
            <span className="px-1 py-px text-[8px] font-bold uppercase rounded bg-zinc-100 text-zinc-400 flex-shrink-0" title="No heading in the article — content before the first heading">Implicit</span>
          )}
          {s.words > 0 && <span className="text-[10px] text-zinc-300 tabular-nums flex-shrink-0">{s.words}w · {pct}%</span>}
        </BodyTag>
        <div className="flex items-center flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
          {s.summary && (
            <button onClick={() => setOpen((v) => !v)} className="p-0.5 text-zinc-300 hover:text-zinc-700 transition-colors" title={open ? "Hide preview" : "Preview section text"}>
              <ChevronDown size={12} strokeWidth={2.5} className={open ? "rotate-180 transition-transform" : "transition-transform"} />
            </button>
          )}
          <button onClick={() => onMove(i, -1)} disabled={i === 0} className="p-0.5 text-zinc-300 hover:text-zinc-700 disabled:opacity-30 transition-colors" title="Move up">
            <ArrowUp size={12} strokeWidth={2.5} />
          </button>
          <button onClick={() => onMove(i, 1)} disabled={last} className="p-0.5 text-zinc-300 hover:text-zinc-700 disabled:opacity-30 transition-colors" title="Move down">
            <ArrowDown size={12} strokeWidth={2.5} />
          </button>
          <button onClick={() => onRemove(i)} className="p-0.5 text-zinc-300 hover:text-red-500 transition-colors" title="Remove section">
            <X size={12} strokeWidth={2.5} />
          </button>
        </div>
      </div>
      <div className="mx-1.5 h-0.5 rounded-full bg-zinc-100 overflow-hidden" title={`${s.words || 0} of ${totalWords} words`}>
        {s.added ? (
          <div className="h-full w-full border-t-2 border-dashed border-indigo-200" />
        ) : (
          <div className="h-full bg-zinc-300" style={{ width: `${pct}%` }} />
        )}
      </div>
      {open && s.summary && (
        <p className="text-[11px] text-zinc-400 leading-relaxed px-1.5 pt-1 pb-0.5 line-clamp-3">{s.summary}…</p>
      )}
    </div>
  );
}

function JumpMarker({ jump, onScroll }) {
  return (
    <button
      onClick={onScroll}
      className="w-full text-left ml-2 pl-2 py-1 border-l-2 border-amber-300 text-[11px] text-amber-600 leading-snug hover:bg-amber-50 rounded-r transition-colors"
      title={`Thought jump between "${jump.between[0]}" and "${jump.between[1]}" — click to see the boundary in the article`}
    >
      ⚠ jump: {jump.why || "no logical bridge"}
    </button>
  );
}

function GapCard({ gap, onAdd, onDismiss }) {
  const badge = SOURCE_BADGE[gap.source] || SOURCE_BADGE.topic;
  return (
    <div className="rounded-lg border border-zinc-100 bg-zinc-50 p-2.5 space-y-1.5">
      <div className="flex items-start justify-between gap-2">
        <span className="text-xs font-bold text-zinc-800 leading-snug">{gap.heading}</span>
        <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase rounded flex-shrink-0 ${badge.cls}`}>{badge.label}</span>
      </div>
      {gap.answers && <p className="text-[11px] text-zinc-500 leading-snug">Answers: “{gap.answers}”</p>}
      {gap.reason && <p className="text-[11px] text-zinc-400 leading-snug">{gap.reason}</p>}
      <div className="flex items-center gap-3 pt-0.5">
        <button onClick={onAdd} className="inline-flex items-center gap-1 text-[11px] font-bold text-zinc-600 hover:text-zinc-900 transition-colors">
          <Plus size={11} strokeWidth={2.5} /> Add section
        </button>
        <button onClick={onDismiss} className="text-[10px] text-zinc-300 hover:text-zinc-500 transition-colors">dismiss</button>
      </div>
    </div>
  );
}

export default function ArticleOutlinePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const articleId = parseInt(id);
  const {
    loading, error, ready, dirty, sections, originalSections, totalWords,
    move, remove, gapsState, findGaps, addGap, dismissGap, jumpAt, toPayload,
  } = useArticleOutline(articleId);

  const [article, setArticle] = useState(null);
  const [staticEditor, setStaticEditor] = useState(null);
  const [contentKey, setContentKey] = useState(0);
  const [contentError, setContentError] = useState(null);
  const [hasCustomComponents, setHasCustomComponents] = useState(false);
  const paneRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const art = await api.getArticle(articleId);
        if (!cancelled) setArticle(art);
      } catch { /* title optional — top bar falls back */ }
    })();
    return () => { cancelled = true; };
  }, [articleId]);

  const loadContent = async () => {
    setContentError(null);
    try {
      const raw = await api.getArticleRawContent(articleId);
      setHasCustomComponents(!!raw.has_custom_components);
      setStaticEditor(createStaticEditor({ plugins: BaseEditorKit, value: htmlToValue(raw.content_html) }));
      setContentKey((k) => k + 1);
    } catch (e) {
      setStaticEditor(null);
      setContentError(e.response?.data?.detail || "Could not load article content");
    }
  };
  useEffect(() => { loadContent(); }, [articleId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ordinal of each section among identically-titled sections in the *original*
  // document order — disambiguates duplicate headings when scrolling to a DOM node.
  const ordinals = useMemo(() => {
    const seen = {};
    const map = {};
    for (const s of originalSections) {
      const k = s.heading.trim();
      map[s.key] = seen[k] || 0;
      seen[k] = (seen[k] || 0) + 1;
    }
    return map;
  }, [originalSections]);

  // CMS headings have no reliable ids — match the rendered static DOM by
  // heading text (exact, then case-insensitive); no match = CMS drift, no-op.
  const scrollToHeading = (s) => {
    const container = paneRef.current;
    if (!container || !s || s.added || s.level === 0) return;
    const target = s.heading.trim();
    const els = [...container.querySelectorAll("h2,h3,h4")];
    let pool = els.filter((el) => el.textContent.trim() === target);
    if (!pool.length) pool = els.filter((el) => el.textContent.trim().toLowerCase() === target.toLowerCase());
    if (!pool.length) return;
    const el = pool[Math.min(ordinals[s.key] ?? 0, pool.length - 1)];
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("bg-amber-100", "rounded", "transition-colors");
    setTimeout(() => el.classList.remove("bg-amber-100", "rounded", "transition-colors"), 1600);
  };

  const goBack = () => {
    if (dirty && !window.confirm("Discard outline edits?")) return;
    navigate(`/dashboard/articles/${id}`);
  };

  const heading = article?.title || "Article outline";

  return (
    <div className="flex flex-col h-full min-h-0 bg-[#F8F9FA]">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-100 bg-white flex-shrink-0 gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <button onClick={goBack} className="p-1.5 hover:bg-zinc-100 rounded-lg text-zinc-400 hover:text-zinc-700 transition-colors flex-shrink-0" title="Back to article">
            <ArrowLeft size={15} strokeWidth={2.5} />
          </button>
          <div className="min-w-0">
            <p className="text-[9px] tracking-[0.2em] uppercase font-bold text-zinc-400">Article · Outline</p>
            <h1 className="text-sm font-bold text-zinc-900 truncate">{heading}</h1>
          </div>
        </div>
        {ready && totalWords > 0 && (
          <span className="text-[11px] text-zinc-400 tabular-nums flex-shrink-0">{sections.length} sections · {totalWords.toLocaleString()} words</span>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-hidden">
        <ResizablePanelGroup direction="horizontal">
          <ResizablePanel defaultSize={40} minSize={25}>
            <div className="h-full min-h-0 flex flex-col">
              <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-1">
                {loading ? (
                  <p className="text-[11px] text-zinc-400">Extracting outline…</p>
                ) : error ? (
                  <p className="text-[11px] text-red-400">{error}</p>
                ) : (
                  <>
                    <p className="text-[10px] text-zinc-400 pb-1">Click a section to see it in the article →</p>
                    {sections.map((s, i) => {
                      const jump = jumpAt(i);
                      return (
                        <div key={s.key}>
                          <SectionRow
                            s={s}
                            i={i}
                            last={i === sections.length - 1}
                            totalWords={totalWords}
                            canScroll={!s.added && s.level !== 0}
                            onScroll={() => scrollToHeading(s)}
                            onMove={move}
                            onRemove={remove}
                          />
                          {jump && <JumpMarker jump={jump} onScroll={() => scrollToHeading(sections[i + 1])} />}
                        </div>
                      );
                    })}

                    <div className="pt-3 space-y-2">
                      {!gapsState.gaps && !gapsState.gapsLoading && (
                        <button onClick={findGaps} className="text-[11px] font-bold text-zinc-400 hover:text-zinc-700 transition-colors">
                          Find coverage gaps →
                        </button>
                      )}
                      {gapsState.gapsLoading && <p className="text-[11px] text-zinc-400">Comparing against real audience questions…</p>}
                      {gapsState.gapError && <p className="text-[11px] text-red-400">{gapsState.gapError}</p>}
                      {gapsState.gaps && (
                        <div className="space-y-2">
                          {gapsState.gaps.ordering_note && (
                            <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-[11px] text-amber-700 leading-snug">
                              ⚠ {gapsState.gaps.ordering_note}
                            </div>
                          )}
                          {gapsState.gaps.missing.length === 0 && !gapsState.gaps.ordering_note && (
                            <p className="text-[11px] text-green-600 font-bold">No uncovered questions — outline covers the real demand.</p>
                          )}
                          {gapsState.gaps.missing.map((gap, i) => (
                            <GapCard key={i} gap={gap} onAdd={() => addGap(gap)} onDismiss={() => dismissGap(gap)} />
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              {ready && (
                <div className="border-t border-zinc-100 bg-white p-3 flex-shrink-0">
                  {dirty ? (
                    <button
                      onClick={() => navigate(`/dashboard/articles/${id}`, { state: { outline: toPayload() } })}
                      className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold text-white rounded-lg transition-colors"
                      style={{ backgroundColor: "#111827" }}
                    >
                      <Sparkles size={12} strokeWidth={2} />
                      Improve from outline
                    </button>
                  ) : (
                    <p className="text-[10px] text-zinc-400">Reorder, remove, or add sections — then rewrite against this structure.</p>
                  )}
                </div>
              )}
            </div>
          </ResizablePanel>

          <ResizableHandle withHandle />

          <ResizablePanel defaultSize={60} minSize={30}>
            <div className="h-full min-h-0 overflow-y-auto bg-white" ref={paneRef}>
              {contentError ? (
                <div className="p-8 max-w-md">
                  <p className="text-xs text-red-400">{contentError}</p>
                  <button onClick={loadContent} className="mt-2 text-[11px] font-bold text-zinc-500 hover:text-zinc-800 transition-colors">Retry</button>
                </div>
              ) : !staticEditor ? (
                <p className="p-8 text-xs text-zinc-400">Loading article…</p>
              ) : (
                <>
                  {hasCustomComponents && (
                    <div className="mx-6 mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 flex items-start gap-2">
                      <AlertTriangle size={13} className="text-amber-500 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                      <p className="text-[11px] text-amber-700 leading-snug">
                        Contains custom components — the outline stays accurate (parsed server-side) but this preview may render them approximately.
                      </p>
                    </div>
                  )}
                  <EditorStatic key={contentKey} editor={staticEditor} className="px-6 sm:px-10 py-6 pb-40 text-base md:text-sm" />
                </>
              )}
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    </div>
  );
}
